import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiX, FiSearch, FiDownload, FiRefreshCw, FiPlus, FiBell, FiEdit, FiTrash2, FiEye, FiUsers, FiMoreVertical } from 'react-icons/fi';
import { extensionsAPI } from '../services/api';
import './Extensions.css';

const Extensions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedExtensions, setSelectedExtensions] = useState([]);

  // Fetch extensions from database
  const fetchExtensions = async () => {
    try {
      setLoading(true);
      const data = await extensionsAPI.getAll();
      
      if (data.success && data.extensions) {
        let extensionsList = data.extensions;
        
        // Process extensions to add notification indicators
        extensionsList = extensionsList.map(ext => {
          const extension = { ...ext };
          
          // Check if extension has been updated recently (within last 7 days)
          const updatedAt = new Date(extension.updatedAt || extension.createdAt);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          
          extension.hasNewVersion = updatedAt > sevenDaysAgo;
          extension.isUpgradeable = extension.isUpgradeable || false;
          
          return extension;
        });
        
        setExtensions(extensionsList);
      } else {
        console.error('Failed to fetch extensions:', data);
        setExtensions([]);
      }
    } catch (error) {
      console.error('Error fetching extensions:', error);
      setExtensions([]);
      toast.error('Failed to load extensions');
    } finally {
      setLoading(false);
    }
  };

  // Initialize extensions data
  useEffect(() => {
    fetchExtensions();
  }, []);

  // Refresh extensions when returning to this page
  useEffect(() => {
    if (location.pathname === '/extensions') {
      fetchExtensions();
    }
  }, [location.pathname]);

  const filterOptions = [
    { value: 'all', label: 'All Extensions' },
    { value: 'whitelabeled', label: 'Whitelabeled' },
    { value: 'source', label: 'Source Extensions' },
    { value: 'new', label: 'New Versions' },
    { value: 'upgradeable', label: 'Upgradeable' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const filteredExtensions = extensions.filter(ext => {
    const matchesSearch = ext.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ext.category && ext.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (ext.description && ext.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'whitelabeled' && ext.isWhitelabeled) ||
                         (filterType === 'source' && ext.isSourceExtension) ||
                         (filterType === 'new' && ext.hasNewVersion) ||
                         (filterType === 'upgradeable' && ext.isUpgradeable) ||
                         (filterType === 'active' && ext.status === 'active') ||
                         (filterType === 'inactive' && ext.status === 'inactive');
    
    return matchesSearch && matchesFilter;
  });

  const handleDownload = async (extension) => {
    try {
      setDownloadingId(extension._id || extension.id);
      
      // Check if extension has a file URL
      if (!extension.fileUrl) {
        toast.error('No file available for download');
        return;
      }

      // If it's a base64 data URL, convert it to a blob and download
      if (extension.fileUrl.startsWith('data:')) {
        // Extract the base64 data from the data URL
        const base64Data = extension.fileUrl.split(',')[1];
        const binaryData = atob(base64Data);
        
        // Convert to Uint8Array
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        
        // Create blob and download
        const blob = new Blob([bytes], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = extension.fileName || `${extension.name.replace(/\s+/g, '-').toLowerCase()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Downloading ${extension.name}`);
      } else {
        // If it's a regular URL, open in new tab
        window.open(extension.fileUrl, '_blank');
        toast.success(`Opening ${extension.name} in new tab`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download extension');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleEdit = (extension) => {
    navigate(`/edit-extension/${extension._id || extension.id}`);
  };

  const handleViewUsers = (extension) => {
    navigate(`/extensions/${extension._id || extension.id}/users`);
  };

  const handleDelete = async (extension) => {
    if (window.confirm(`Are you sure you want to delete "${extension.name}"? This action cannot be undone.`)) {
      try {
        const response = await extensionsAPI.delete(extension._id || extension.id);
        if (response.success) {
          toast.success('Extension deleted successfully');
          fetchExtensions();
        } else {
          toast.error(response.message || 'Failed to delete extension');
        }
      } catch (error) {
        console.error('Error deleting extension:', error);
        toast.error('Failed to delete extension');
      }
    }
  };

  const handleMenuClick = (event) => {
    // Close all other open menus
    const allMenus = document.querySelectorAll('.menu-gen');
    allMenus.forEach(menu => {
      if (menu !== event.target.nextElementSibling) {
        menu.style.display = 'none';
      }
    });

    // Toggle the clicked menu
    const menu = event.target.nextElementSibling;
    if (menu) {
      menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
    }
  };

  const handleRefresh = async () => {
    await fetchExtensions();
    toast.success('Extensions refreshed successfully');
  };

  const handleCreateExtension = () => {
    navigate('/create-extension');
  };

  const handleSelectAll = () => {
    if (selectedExtensions.length === filteredExtensions.length) {
      setSelectedExtensions([]);
    } else {
      setSelectedExtensions(filteredExtensions.map(ext => ext._id || ext.id));
    }
  };

  const handleSelectExtension = (extensionId) => {
    setSelectedExtensions(prev => 
      prev.includes(extensionId) 
        ? prev.filter(id => id !== extensionId)
        : [...prev, extensionId]
    );
  };

  const closeModal = () => {
    navigate('/dashboard');
  };

  // Count notifications
  const newVersionCount = extensions.filter(ext => ext.hasNewVersion).length;
  const upgradeableCount = extensions.filter(ext => ext.isUpgradeable).length;

  if (loading) {
    return (
      <div className="extensions-container">
        <div className="page-header">
          <div className="page-title">
            <h2>Extensions</h2>
            <p>Manage your Chrome extensions and whitelabeled versions</p>
          </div>
          <button className="close-btn" onClick={closeModal}>
            <FiX />
          </button>
        </div>
        <div className="extensions-content">
          <div className="loading-spinner">Loading extensions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extensions-container">
      <div className="page-header">
        <div className="page-title">
          <h2>Extensions</h2>
          <p>Manage your Chrome extensions and whitelabeled versions</p>
        </div>
        <button className="close-btn" onClick={closeModal}>
          <FiX />
        </button>
      </div>

      <div className="extensions-content">
        {/* Header Controls */}
        <div className="extensions-header">
          <div className="search-filters">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search extensions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                  {option.value === 'new' && newVersionCount > 0 && ` (${newVersionCount})`}
                  {option.value === 'upgradeable' && upgradeableCount > 0 && ` (${upgradeableCount})`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="action-buttons">
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <FiRefreshCw />
              Refresh
            </button>
            <button className="btn btn-primary" onClick={handleCreateExtension}>
              <FiPlus />
              Create Extension
            </button>
          </div>
        </div>

        {/* Extensions Table */}
        <div className="extensions-table-container">
          <table className="extensions-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedExtensions.length === filteredExtensions.length && filteredExtensions.length > 0}
                    onChange={handleSelectAll}
                    className="select-checkbox"
                  />
                </th>
                <th>Extension</th>
                <th>Category</th>
                <th>Version</th>
                <th>Status</th>
                <th>Plans</th>
                <th>Partners</th>
                <th>Users</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExtensions.length > 0 ? (
                filteredExtensions.map(extension => {
                  const isSelected = selectedExtensions.includes(extension._id || extension.id);
                  return (
                    <tr key={extension._id || extension.id} className={extension.hasNewVersion ? 'new-version' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectExtension(extension._id || extension.id)}
                          className="select-checkbox"
                        />
                      </td>
                      <td>
                        <div className="extension-info">
                          <img 
                            src={extension.icon || 'https://via.placeholder.com/40x40/039BE5/FFFFFF?text=FC'} 
                            alt={extension.name}
                            className="extension-icon"
                          />
                          <div className="extension-details">
                            <div className="extension-name">
                              {extension.name}
                              {extension.hasNewVersion && (
                                <span className="new-version-badge">
                                  <FiBell />
                                  New
                                </span>
                              )}
                              {extension.isWhitelabeled && (
                                <span className="whitelabel-badge">Whitelabel</span>
                              )}
                              {extension.isSourceExtension && (
                                <span className="source-badge">Source</span>
                              )}
                            </div>
                            {extension.description && (
                              <div className="extension-description">{extension.description}</div>
                            )}
                            {extension.repoMention && (
                              <div className="extension-repo">{extension.repoMention}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="category-badge">{extension.category || 'N/A'}</span>
                      </td>
                      <td>
                        <span className={`version-badge ${extension.isUpgradeable ? 'upgradeable' : ''}`}>
                          {extension.version || '1.0.0'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${extension.status || 'active'}`}>
                          {extension.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <span className="plans-count">{extension.plans?.length || 0} plans</span>
                      </td>
                      <td>
                        <span className="partners-count">{extension.assignedPartners?.length || 0} partners</span>
                      </td>
                      <td>
                        <span className="users-count">{extension.totalUsers || 0} users</span>
                      </td>
                      <td>
                        <span className="date-info">
                          {extension.createdAt ? new Date(extension.createdAt).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className="date-info">
                          {extension.updatedAt ? new Date(extension.updatedAt).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td>
                        <div className="menu-options">
                          <button className="btnOptions" data-title="Action" onClick={handleMenuClick}>
                            <FiMoreVertical />
                          </button>
                          <div className="menu-gen">
                            <button 
                              className="btn btn-medium"
                              onClick={() => handleDownload(extension)}
                              disabled={downloadingId === (extension._id || extension.id)}
                            >
                              <FiDownload />
                              {downloadingId === (extension._id || extension.id) ? 'Downloading...' : 'Download'}
                            </button>
                            <button 
                              className="btn btn-medium"
                              onClick={() => navigate(`/details/${extension._id || extension.id}`)}
                            >
                              <FiEye />
                              Details
                            </button>
                            <button 
                              className="btn btn-medium"
                              onClick={() => handleViewUsers(extension)}
                            >
                              <FiUsers />
                              View End Users
                            </button>
                            <button 
                              className="btn btn-medium"
                              onClick={() => handleEdit(extension)}
                            >
                              <FiEdit />
                              Edit
                            </button>
                            <button 
                              className="btn btn-medium"
                              onClick={() => handleDelete(extension)}
                            >
                              <FiTrash2 />
                              Delete
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="no-data">
                    <div className="no-data-content">
                      {searchTerm || filterType !== 'all' 
                        ? 'No extensions match your search criteria' 
                        : 'No extensions found. Click "Create Extension" to add your first extension.'}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bulk Actions */}
        {selectedExtensions.length > 0 && (
          <div className="bulk-actions">
            <div className="bulk-info">
              {selectedExtensions.length} extension(s) selected
            </div>
            <div className="bulk-buttons">
              <button className="btn btn-secondary">
                <FiDownload />
                Download Selected
              </button>
              <button className="btn btn-danger">
                <FiTrash2 />
                Delete Selected
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Extensions; 